import { useEffect, useMemo } from "react"

import { useAnnotationsWithMetricOutputsTransport } from "@/human-feedback/transport"
import { useTransportInfiniteQuery } from "@/transport"

export function useAnnotatedTestCaseConversationIds({
  enabled,
  metricId,
  testCaseId,
  testSetId,
}: {
  enabled: boolean
  metricId: string
  testCaseId: string
  testSetId: string
}) {
  const simulationsRequest = useMemo(
    () => ({
      limit: 100,
      filters: {
        test_set_filters: [testSetId],
        test_case_filters: [testCaseId],
      },
    }),
    [testCaseId, testSetId],
  )
  const simulationsQuery = useTransportInfiniteQuery({
    operation: "listSimulatedConversationsKeyset",
    request: simulationsRequest,
    getNextRequest: (lastPage, _allPages, previousRequest) =>
      lastPage.next_cursor
        ? { ...previousRequest, cursor: lastPage.next_cursor }
        : null,
    options: { enabled },
  })
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = simulationsQuery

  useEffect(() => {
    if (enabled && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage()
    }
  }, [enabled, fetchNextPage, hasNextPage, isFetchingNextPage])

  const simulationIds = useMemo(
    () =>
      Array.from(
        new Set(
          (simulationsQuery.data?.pages ?? []).flatMap((page) =>
            page.items.map((item) => item.simulation_output_id),
          ),
        ),
      ),
    [simulationsQuery.data?.pages],
  )
  const simulationIdSet = useMemo(() => new Set(simulationIds), [simulationIds])
  const hasLoadedAllSimulations =
    enabled &&
    !simulationsQuery.isLoading &&
    !isFetchingNextPage &&
    !hasNextPage
  const annotationsQuery = useAnnotationsWithMetricOutputsTransport({
    metricId: metricId || undefined,
    simulationOutputIds: simulationIds,
    enabled:
      hasLoadedAllSimulations && Boolean(metricId) && simulationIds.length > 0,
  })
  const conversationIds = useMemo(
    () =>
      Array.from(
        new Set(
          (annotationsQuery.data?.annotations[metricId] ?? [])
            .filter(
              (annotation) =>
                !annotation.archived &&
                simulationIdSet.has(annotation.simulation_output_id),
            )
            .map((annotation) => annotation.simulation_output_id),
        ),
      ),
    [annotationsQuery.data?.annotations, metricId, simulationIdSet],
  )

  return {
    conversationIds,
    simulationIds,
    isLoading:
      enabled &&
      (simulationsQuery.isLoading ||
        isFetchingNextPage ||
        Boolean(hasNextPage) ||
        (simulationIds.length > 0 && annotationsQuery.isLoading)),
  }
}
